// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Bootstrap and the screen machine: which screen is up, the canvas, the thumb, and the clock.
//
// The rules live in rules.ts, the board in map.ts, the search in bot.ts, the drawing in render.ts,
// the gesture in plan.ts and the scripted first five minutes in tutorial.ts. This file owns none of
// those and only decides what is on screen.

import '@ben-gy/game-engine/mobile.css';
import './styles/main.css';

import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { newSeed } from '@ben-gy/game-engine/rng';

import { escapeHtml } from './dom';
import { DEFAULT_MODE, MODE_IDS, modeOf, type ModeId } from './modes';
import { DEFAULT_TIER, chooseOrders, tierOf, type TierId } from './bot';
import { MOVE, RUSH, STALK, holdAll, type SideOrders, type Stance } from './orders';
import {
  DIRS,
  aliveCount,
  commitTurn,
  createMatch,
  mastsFor,
  type MatchState,
  type Replay,
  type Side,
} from './rules';
import { STANCES, UNITS, WINDOW_TICKS } from './tuning';
import { draw, layout, planFrame, replayFrame, type View } from './render';
import { extendPath, handleAt, tileAt, unitAtPointer, watchFromPointer } from './plan';
import { STEPS, createRun, type TutorialRun } from './tutorial';
import { ATTRIB, aboutHtml, helpHtml, menuHtml, resultsHtml, type SideStats } from './ui';
import { PATCHES } from './cues';
import { SEAT_NAME } from './palette';

const SLUG = 'standoff';
const YOU: Side = 0;

const app = document.querySelector<HTMLElement>('#app')!;
const store = createStore(SLUG);
// The patches MUST be passed here: play() on an unknown cue is a silent no-op, so a missing patch is
// an inaudible bug rather than an error.
const sfx = createSfx({ muted: store.get<boolean>('muted', false) ?? false, patches: PATCHES });
const reduced = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

let unlocked = false;
const unlock = (): void => {
  if (unlocked) return;
  unlocked = true;
  try {
    sfx.unlock();
  } catch {
    /* audio unlock is best-effort and must never break a tap */
  }
};
window.addEventListener('pointerdown', unlock, { once: true });
window.addEventListener('keydown', unlock, { once: true });
const ping = (cue: string): void => {
  try {
    sfx.play(cue);
  } catch {
    /* a missing audio context must never break the game */
  }
};

let modeId: ModeId = modeOf(store.get<string>('mode', DEFAULT_MODE)).id;
let tier: TierId = tierOf(store.get<string>('tier', DEFAULT_TIER)).id;

// ── screen lifecycle ────────────────────────────────────────────────────────────────────────────
const cleanups: Array<() => void> = [];
const onCleanup = (fn: () => void): void => void cleanups.push(fn);
function teardown(): void {
  for (const fn of cleanups.splice(0)) {
    try {
      fn();
    } catch {
      /* a failing teardown must never block the next screen */
    }
  }
}
const setPlaying = (on: boolean): void => void document.body.classList.toggle('playing', on);

let content: HTMLElement;
function mountShell(): void {
  app.innerHTML = `<main class="main-content" id="content"></main><footer class="site-footer">${ATTRIB}</footer>`;
  content = document.getElementById('content')!;
}

// ── overlay panels ──────────────────────────────────────────────────────────────────────────────

/**
 * A dismissible panel, with ALL FOUR exits wired: the close button, a tap outside, Escape, and a
 * backdrop that is genuinely reachable. An overlay with one way out is a game you cannot play, and
 * the backdrop listener is `pointerdown` so mouse, touch and pen are one path.
 */
function openPanel(title: string, body: string): void {
  const el = document.createElement('div');
  el.className = 'overlay';
  el.innerHTML = `<div class="backdrop"></div>
    <div class="panel" role="dialog" aria-modal="true" aria-label="${escapeHtml(title)}">
      <header><h2>${escapeHtml(title)}</h2><button class="x" aria-label="Close">&times;</button></header>
      ${body}
    </div>`;
  const close = (): void => {
    el.remove();
    document.removeEventListener('keydown', onKey);
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') close();
  };
  el.querySelector('.x')?.addEventListener('click', close);
  el.querySelector('.backdrop')?.addEventListener('pointerdown', close);
  document.addEventListener('keydown', onKey);
  app.appendChild(el);
}

// ── the menu ────────────────────────────────────────────────────────────────────────────────────

const bestKey = (id: string): string => `wins_${id}`;
const winsIn = (id: string): number => store.get<number>(bestKey(id), 0) ?? 0;

function showMenu(): void {
  teardown();
  setPlaying(false);
  mountShell();
  content.innerHTML = menuHtml(modeOf(modeId), tier, winsIn(modeId));

  content.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach((b) =>
    b.addEventListener('click', () => {
      modeId = modeOf(b.dataset.mode).id;
      store.set('mode', modeId);
      ping('select');
      showMenu();
    }),
  );
  content.querySelectorAll<HTMLButtonElement>('[data-tier]').forEach((b) =>
    b.addEventListener('click', () => {
      tier = tierOf(b.dataset.tier).id;
      store.set('tier', tier);
      ping('select');
      showMenu();
    }),
  );
  content.querySelector('#play')?.addEventListener('click', () => {
    ping('commit');
    startMatch(newSeed());
  });
  // "How to play" opens the TUTORIAL, not a card of text. The card still exists, as a reference the
  // player can open from inside it, but it is never the thing that teaches the game.
  content.querySelector('#how')?.addEventListener('click', () => startTutorial());
  content.querySelector('#rules')?.addEventListener('click', () => openPanel('How to play', helpHtml()));
  content.querySelector('#about')?.addEventListener('click', () => openPanel('About Standoff', aboutHtml()));

  const mute = content.querySelector<HTMLButtonElement>('#mute');
  const paintMute = (): void => {
    if (!mute) return;
    mute.textContent = sfx.muted() ? 'Sound off' : 'Sound on';
    mute.setAttribute('aria-pressed', String(sfx.muted()));
  };
  paintMute();
  mute?.addEventListener('click', () => {
    sfx.setMuted(!sfx.muted());
    store.set('muted', sfx.muted());
    paintMute();
  });
}

// ── the match screen ────────────────────────────────────────────────────────────────────────────

type Phase = 'plan' | 'replay' | 'over';

interface Live {
  s: MatchState;
  orders: SideOrders;
  selected: number;
  phase: Phase;
  rep: Replay | null;
  tick: number;
  threat: boolean;
  tutorial: TutorialRun | null;
  stats: SideStats[];
  seed: number;
}

let live: Live | null = null;

function freshStats(s: MatchState): SideStats[] {
  return ([0, 1] as Side[]).map((side) => ({
    hold: 0,
    units: s.units
      .filter((u) => u.side === side)
      .map((u) => ({ name: u.spec.name, glyph: u.spec.glyph, alive: true, shots: 0, kills: 0, walked: 0 })),
  }));
}

function startMatch(seed: number, tutorial = false): void {
  teardown();
  setPlaying(true);
  mountShell();
  const s = createMatch(seed, tutorial ? 'crossfire' : modeId);
  live = {
    s,
    orders: holdAll(s.mode.units),
    selected: 0,
    phase: 'plan',
    rep: null,
    tick: 0,
    threat: true,
    tutorial: tutorial ? createRun() : null,
    stats: freshStats(s),
    seed,
  };
  mountBoard();
}

function mountBoard(): void {
  const l = live;
  if (!l) return;
  content.innerHTML = `
    <div class="hud">
      <button class="icon" id="quit" aria-label="Back to menu">&larr;</button>
      <div class="scores">
        <span class="score s0"><i></i><b id="h0">0</b></span>
        <span class="turn" id="turnlabel">Turn 1</span>
        <span class="score s1"><i></i><b id="h1">0</b></span>
      </div>
      <button class="icon" id="threat" aria-label="Toggle threat overlay" aria-pressed="true">&#9673;</button>
    </div>
    <div class="board-wrap"><canvas id="board"></canvas></div>
    <div class="coach" id="coach" hidden></div>
    <div class="dock" id="dock"></div>`;

  const canvas = content.querySelector<HTMLCanvasElement>('#board')!;
  const ctx = canvas.getContext('2d')!;
  let view: View = { x: 0, y: 0, cell: 10 };

  const resize = (): void => {
    const wrap = canvas.parentElement!;
    const r = wrap.getBoundingClientRect();
    // A transient 0-size measurement yields NaN world coordinates and silently eats every tap, so
    // ignore it and wait for the next frame rather than computing a scale from nothing.
    if (r.width < 2 || r.height < 2) return;
    const dpr = Math.min(3, window.devicePixelRatio || 1);
    canvas.width = Math.round(r.width * dpr);
    canvas.height = Math.round(r.height * dpr);
    canvas.style.width = `${r.width}px`;
    canvas.style.height = `${r.height}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    view = layout(r.width, r.height);
    paint();
  };
  const ro = new ResizeObserver(resize);
  ro.observe(canvas.parentElement!);
  onCleanup(() => ro.disconnect());
  // A backgrounded tab reports a 0-height wrapper; a plain timeout re-measures without depending on
  // rAF, which never fires in a hidden tab.
  const kick = setTimeout(resize, 0);
  onCleanup(() => clearTimeout(kick));

  function paint(): void {
    const s = live;
    if (!s) return;
    const frame =
      s.phase === 'replay' && s.rep
        ? replayFrame(s.rep, s.tick, YOU, s.s.mode.fog, s.s.units)
        : planFrame(s.s, YOU, s.orders, s.selected, s.threat ? 'theirs' : 'mine');
    if (s.phase !== 'replay') frame.masts = mastsFor(s.s.mode, s.s.turn);
    draw(ctx, view, s.s.map, frame, { threat: s.threat, reduced });
    const h0 = document.getElementById('h0');
    const h1 = document.getElementById('h1');
    if (h0) h0.textContent = String(Math.round(frame.hold[0] / 20));
    if (h1) h1.textContent = String(Math.round(frame.hold[1] / 20));
    const tl = document.getElementById('turnlabel');
    if (tl) tl.textContent = `Turn ${Math.min(s.s.turn + 1, s.s.mode.turns)}/${s.s.mode.turns}`;
  }

  // ── the thumb ─────────────────────────────────────────────────────────────────────────────────
  let grab: 'path' | 'watch' | null = null;
  let pointerId = -1;

  const endOf = (unitIndex: number): { x: number; y: number } => {
    const u = live!.s.units[unitIndex];
    const path = live!.orders[u.n].path;
    if (!path.length) return { x: u.x, y: u.y };
    const t = path[path.length - 1];
    return { x: (t % 12) + 0.5, y: Math.floor(t / 12) + 0.5 };
  };

  const localPoint = (e: PointerEvent): { x: number; y: number } => {
    const r = canvas.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const onDown = (e: PointerEvent): void => {
    const s = live;
    if (!s || s.phase !== 'plan') return;
    const p = localPoint(e);
    canvas.setPointerCapture(e.pointerId);
    pointerId = e.pointerId;

    // The watch handle wins over everything, because it sits over the board and is the smaller
    // target of the two.
    const sel = s.s.units[s.selected];
    if (sel && sel.side === YOU && sel.alive) {
      const h = handleAt(view, endOf(s.selected), s.orders[sel.n].watch < 0 ? sel.face : s.orders[sel.n].watch, DIRS);
      if (Math.hypot(p.x - h.x, p.y - h.y) < 44) {
        grab = 'watch';
        return;
      }
    }

    const hit = unitAtPointer(view, s.s.units, YOU, p.x, p.y);
    if (hit >= 0) {
      s.selected = hit;
      grab = null;
      ping('select');
      renderDock();
      paint();
      return;
    }
    if (!allowed('path')) return;
    grab = 'path';
    onMove(e);
  };

  const onMove = (e: PointerEvent): void => {
    const s = live;
    if (!s || s.phase !== 'plan' || !grab || e.pointerId !== pointerId) return;
    const u = s.s.units[s.selected];
    if (!u || u.side !== YOU || !u.alive) return;
    const p = localPoint(e);
    const o = s.orders[u.n];

    if (grab === 'watch') {
      if (!allowed('watch')) return;
      const w = watchFromPointer(view, endOf(s.selected), p.x, p.y);
      if (w !== o.watch) {
        o.watch = w;
        paint();
      }
      return;
    }
    const finger = tileAt(view, p.x, p.y);
    const from = (Math.floor(u.y) * 12 + Math.floor(u.x)) | 0;
    const next = extendPath(s.s.map, from, o.path, finger);
    if (next.length !== o.path.length || next[next.length - 1] !== o.path[o.path.length - 1]) {
      o.path = next;
      paint();
      renderDock();
    }
  };

  const onUp = (e: PointerEvent): void => {
    if (e.pointerId !== pointerId) return;
    const s = live;
    if (grab === 'path') {
      ping('waypoint');
      if (s?.s.units[s.selected] && s.orders[s.s.units[s.selected].n].path.length) advance('path');
    }
    if (grab === 'watch') advance('watch');
    grab = null;
    pointerId = -1;
    renderDock();
    paint();
  };

  canvas.addEventListener('pointerdown', onDown);
  canvas.addEventListener('pointermove', onMove);
  canvas.addEventListener('pointerup', onUp);
  // An incoming call fires pointercancel, not pointerup. Treated as an abort so a half-drawn route
  // does not stay glued to a finger that is no longer there.
  canvas.addEventListener('pointercancel', onUp);
  onCleanup(() => {
    canvas.removeEventListener('pointerdown', onDown);
    canvas.removeEventListener('pointermove', onMove);
    canvas.removeEventListener('pointerup', onUp);
    canvas.removeEventListener('pointercancel', onUp);
  });

  /** Is this gesture live right now? During the tutorial everything else is refused. */
  function allowed(kind: 'path' | 'watch' | 'stance' | 'go'): boolean {
    const s = live;
    if (!s?.tutorial || s.tutorial.done()) return true;
    if (s.tutorial.allows(kind)) return true;
    coach(s.tutorial, `Not yet — ${s.tutorial.step()?.copy ?? ''}`);
    return false;
  }

  /** Tell the tutorial a gesture landed, and move the coach on if it was the one being asked for. */
  function advance(kind: 'path' | 'watch' | 'stance' | 'go'): void {
    const s = live;
    if (!s?.tutorial || s.tutorial.done()) return;
    const was = s.tutorial.index;
    s.tutorial.did(kind);
    if (s.tutorial.index !== was) {
      if (s.tutorial.done()) store.set('taught', true);
      coach(s.tutorial, s.tutorial.done() ? 'That is the whole game. Off you go.' : undefined);
    }
  }

  // ── the dock ──────────────────────────────────────────────────────────────────────────────────
  function renderDock(): void {
    const s = live;
    if (!s) return;
    const dock = document.getElementById('dock');
    if (!dock) return;
    if (s.phase === 'replay') {
      dock.innerHTML = `<div class="replaybar"><span>Watching the five seconds…</span>
        <button class="primary" id="skip">Skip</button></div>`;
      dock.querySelector('#skip')?.addEventListener('click', () => endReplay());
      return;
    }
    if (s.phase === 'over') {
      dock.innerHTML = '';
      return;
    }
    const mine = s.s.units.map((u, i) => ({ u, i })).filter((x) => x.u.side === YOU);
    const sel = s.s.units[s.selected];
    const stance = sel ? s.orders[sel.n].stance : STALK;
    dock.innerHTML = `
      <div class="chips">
        ${mine
          .map(
            ({ u, i }) => `<button class="chip ${i === s.selected ? 'on' : ''} ${u.alive ? '' : 'dead'}"
              data-unit="${i}" ${u.alive ? '' : 'disabled'}>
              <b>${u.spec.glyph} ${u.spec.name}</b>
              <span>${u.alive ? (s.orders[u.n].path.length ? 'route set' : 'holding') : 'lost'}</span></button>`,
          )
          .join('')}
      </div>
      <div class="stances" role="radiogroup" aria-label="Stance">
        ${STANCES.map(
          (st, i) => `<button class="stance ${i === stance ? 'on' : ''}" data-stance="${i}" role="radio"
            aria-checked="${i === stance}">${st.name}</button>`,
        ).join('')}
        <button class="icon clear" id="clear" aria-label="Clear this unit's route">&#8630;</button>
      </div>
      <button class="primary go" id="go">Run the five seconds</button>`;

    dock.querySelectorAll<HTMLButtonElement>('[data-unit]').forEach((b) =>
      b.addEventListener('click', () => {
        s.selected = Number(b.dataset.unit);
        ping('select');
        renderDock();
        paint();
      }),
    );
    dock.querySelectorAll<HTMLButtonElement>('[data-stance]').forEach((b) =>
      b.addEventListener('click', () => {
        const u = s.s.units[s.selected];
        if (!u) return;
        if (!allowed('stance')) return;
        s.orders[u.n].stance = Number(b.dataset.stance) as Stance;
        ping('stance');
        advance('stance');
        renderDock();
        paint();
      }),
    );
    dock.querySelector('#clear')?.addEventListener('click', () => {
      const u = s.s.units[s.selected];
      if (!u) return;
      s.orders[u.n] = { path: [], watch: -1, stance: s.orders[u.n].stance };
      renderDock();
      paint();
    });
    dock.querySelector('#go')?.addEventListener('click', () => runTurn());
  }

  // ── running a turn ────────────────────────────────────────────────────────────────────────────
  function runTurn(): void {
    const s = live;
    if (!s || s.phase !== 'plan') return;
    if (!allowed('go')) return;
    ping('commit');
    const foe = chooseOrders(s.s, 1, tierOf(tier), s.seed);
    const before = s.s.units.map((u) => ({ x: u.x, y: u.y, alive: u.alive }));
    const rep = commitTurn(s.s, s.orders, foe.orders);
    recordStats(s, rep, before);
    s.rep = rep;
    s.tick = 0;
    s.phase = 'replay';
    renderDock();
    playReplay();
  }

  let timer = 0;
  function playReplay(): void {
    const s = live;
    if (!s || !s.rep) return;
    const rep = s.rep;
    const fired = new Set<number>();
    // setInterval, not rAF: a backgrounded tab freezes rAF for ever, and a replay that never ends is
    // a game that never continues.
    timer = window.setInterval(() => {
      const cur = live;
      if (!cur || cur.phase !== 'replay') return;
      cur.tick += reduced ? 4 : 1;
      for (const e of rep.events) {
        if (e.tick <= cur.tick && !fired.has(e.tick * 10 + (e.kind === 'kill' ? 1 : 0))) {
          if (e.kind === 'fire') ping('fire');
          if (e.kind === 'kill') ping('kill');
          fired.add(e.tick * 10 + (e.kind === 'kill' ? 1 : 0));
        }
      }
      paint();
      if (cur.tick >= rep.ticks) endReplay();
    }, 1000 / 20);
    onCleanup(() => clearInterval(timer));
  }

  function endReplay(): void {
    const s = live;
    if (!s) return;
    clearInterval(timer);
    s.phase = s.s.over ? 'over' : 'plan';
    s.tick = 0;
    s.rep = null;
    if (s.s.over) {
      showResults();
      return;
    }
    s.orders = holdAll(s.s.mode.units);
    advance('go');
    renderDock();
    coachStep();
    paint();
  }

  // ── the tutorial coach ────────────────────────────────────────────────────────────────────────
  function coach(run: TutorialRun, override?: string): void {
    const el = document.getElementById('coach');
    if (!el) return;
    const step = run.step();
    if (!step && !override) {
      el.hidden = true;
      return;
    }
    el.hidden = false;
    el.innerHTML = `<p>${escapeHtml(override ?? step!.copy)}</p>
      <button class="link" id="skiptut">Skip</button>`;
    el.querySelector('#skiptut')?.addEventListener('click', () => {
      run.skip();
      store.set('taught', true);
      el.hidden = true;
    });
  }
  function coachStep(): void {
    const s = live;
    if (s?.tutorial) coach(s.tutorial);
  }

  document.getElementById('quit')?.addEventListener('click', () => showMenu());
  const threatBtn = document.getElementById('threat');
  threatBtn?.addEventListener('click', () => {
    const s = live;
    if (!s) return;
    s.threat = !s.threat;
    threatBtn.setAttribute('aria-pressed', String(s.threat));
    paint();
  });

  renderDock();
  coachStep();
  resize();
}

/** Per-unit shots, kills and distance — what the results screen shows for BOTH sides. */
function recordStats(l: Live, rep: Replay, before: Array<{ x: number; y: number; alive: boolean }>): void {
  for (const e of rep.events) {
    if (e.kind === 'fire') {
      const shooter = l.s.units[e.u];
      const victim = l.s.units[e.v];
      l.stats[shooter.side].units[shooter.n].shots++;
      if (victim) l.stats[shooter.side].units[shooter.n].kills++;
    }
  }
  l.s.units.forEach((u, i) => {
    const b = before[i];
    l.stats[u.side].units[u.n].alive = u.alive;
    l.stats[u.side].units[u.n].walked += Math.hypot(u.x - b.x, u.y - b.y);
  });
  l.stats[0].hold = l.s.hold[0];
  l.stats[1].hold = l.s.hold[1];
}

function showResults(): void {
  const l = live;
  if (!l) return;
  teardown();
  setPlaying(false);
  mountShell();
  if (l.s.winner === YOU) {
    ping('win');
    store.set(bestKey(l.s.modeId), winsIn(l.s.modeId) + 1);
  } else if (l.s.winner >= 0) ping('lose');

  const tally: [number, number] = [aliveCount(l.s, 0), aliveCount(l.s, 1)];
  content.innerHTML = resultsHtml(l.s, YOU, tally, l.stats);
  content.querySelector('#again')?.addEventListener('click', () => startMatch(newSeed()));
  content.querySelector('#menu')?.addEventListener('click', () => showMenu());
  content.querySelector('#feedback')?.addEventListener('click', (e) =>
    window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }),
  );
  content.querySelector('#share')?.addEventListener('click', async () => {
    const url = `${location.origin}${location.pathname}?m=${l.s.modeId}&s=${l.seed}`;
    try {
      if (navigator.share) await navigator.share({ title: 'Standoff', url });
      else await navigator.clipboard.writeText(url);
    } catch {
      /* a refused share sheet is not an error worth showing */
    }
  });
}

// ── the tutorial ────────────────────────────────────────────────────────────────────────────────

function startTutorial(): void {
  startMatch(4242, true);
}

// ── boot ────────────────────────────────────────────────────────────────────────────────────────

hardenViewport();
mountShell();

const params = new URLSearchParams(location.search);
const sharedSeed = Number(params.get('s'));
if (params.get('m')) modeId = modeOf(params.get('m')).id;
if (Number.isFinite(sharedSeed) && sharedSeed > 0) startMatch(sharedSeed >>> 0);
else if (!store.get<boolean>('taught', false)) startTutorial();
else showMenu();

void MODE_IDS;
void SEAT_NAME;
void UNITS;
void WINDOW_TICKS;
void MOVE;
void RUSH;
void STEPS;
